import { Lightbulb, PoundSterling, ArrowRight, Lock } from "lucide-react";

export const PUBLIC_FOLDERS = [
  {
    id: "driving",
    label: "See What We Found",
    teaser: "Your property's potential at a glance",
    icon: Lightbulb,
    color: "border-l-cipher-green",
    activeBg: "bg-cipher-green/[0.06]",
    iconColor: "text-cipher-green",
    free: true,
  },
  {
    id: "meaning",
    label: "How to Unlock Value",
    teaser: "Targeted ways to unlock value",
    icon: PoundSterling,
    color: "border-l-cipher-gold",
    activeBg: "bg-cipher-gold/[0.05]",
    iconColor: "text-cipher-gold",
  },
  {
    id: "next",
    label: "Your Roadmap to Action",
    teaser: "Where to start and what to expect",
    icon: ArrowRight,
    color: "border-l-blue-400",
    activeBg: "bg-blue-400/[0.05]",
    iconColor: "text-blue-400",
  },
];

export const DEFAULT_FOLDERS = [
  {
    id: "possible",
    label: "What's Possible",
    teaser: "Opportunity zones",
    icon: Lightbulb,
    color: "border-l-cipher-green",
    activeBg: "bg-cipher-green/[0.08]",
    iconColor: "text-cipher-green",
  },
  {
    id: "worth",
    label: "What It's Worth",
    teaser: "Value & ROI",
    icon: PoundSterling,
    color: "border-l-cipher-gold",
    activeBg: "bg-cipher-gold/[0.06]",
    iconColor: "text-cipher-gold",
  },
  {
    id: "next",
    label: "What To Do",
    teaser: "Action plan",
    icon: ArrowRight,
    color: "border-l-blue-400",
    activeBg: "bg-blue-400/[0.06]",
    iconColor: "text-blue-400",
  },
];

export function FolderStack({ activeFolder, onFolderChange, folders, children, disabled, lockedFolders = [] }) {
  const folderConfig = folders || DEFAULT_FOLDERS;

  return (
    <div className="space-y-0" data-testid="folder-stack">
      {folderConfig.map((folder) => {
        const isActive = activeFolder === folder.id;
        const isLocked = lockedFolders.includes(folder.id);
        const isFree = folder.free && !isLocked;
        const Icon = folder.icon;

        return (
          <div key={folder.id} data-testid={`folder-${folder.id}`}>
            {/* Folder Tab */}
            <button
              onClick={() => onFolderChange(isActive ? null : folder.id)}
              className={`
                w-full flex items-center gap-3 px-4
                border-l-[3px] ${folder.color}
                transition-all duration-300 ease-out
                ${isLocked ? 'opacity-40 cursor-default' : ''}
                ${isActive
                  ? `${folder.activeBg} rounded-t-xl py-3.5 mb-0`
                  : isFree
                    ? "bg-white/[0.05] hover:bg-white/[0.08] rounded-lg py-2.5 mb-1 ring-1 ring-cipher-green/20"
                    : "bg-white/[0.025] hover:bg-white/[0.05] rounded-lg py-2.5 mb-1"
                }
              `}
              data-testid={`folder-tab-${folder.id}`}
            >
              <div
                className={`rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                  isActive ? "w-9 h-9 bg-white/10" : isFree ? "w-7 h-7 bg-cipher-green/10" : "w-7 h-7 bg-white/5"
                }`}
              >
                {isLocked ? (
                  <Lock className="w-3.5 h-3.5 text-white/20" />
                ) : (
                  <Icon
                    className={`transition-colors duration-300 ${
                      isActive ? `w-4.5 h-4.5 ${folder.iconColor}` : isFree ? `w-3.5 h-3.5 ${folder.iconColor}` : "w-3.5 h-3.5 text-white/25"
                    }`}
                  />
                )}
              </div>
              <div className="flex-1 text-left min-w-0">
                <p
                  className={`font-medium transition-all duration-300 ${
                    isActive ? "text-[15px] text-white" : "text-sm text-white/50"
                  }`}
                >
                  {folder.label}
                </p>
                {!isActive && folder.teaser && (
                  <p className="text-[10px] text-white/25 mt-0.5 truncate">
                    {isLocked ? "Unlock to access" : folder.teaser}
                  </p>
                )}
              </div>
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isActive ? "rotate-90 bg-white/10" : "bg-transparent"
                }`}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  className={`transition-colors duration-300 ${
                    isActive ? folder.iconColor : "text-white/20"
                  }`}
                >
                  <path
                    d="M3.5 2L6.5 5L3.5 8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>

            {/* Expanded Content */}
            {isActive && !isLocked && (
              <div
                className={`
                  ${folder.activeBg} border-l-[3px] ${folder.color}
                  rounded-b-xl px-4 pb-5 pt-2 mb-1
                  animate-in fade-in slide-in-from-top-1 duration-300
                `}
                data-testid={`folder-content-${folder.id}`}
              >
                {children[folder.id]}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
